(() => {
  if (customElements.get('bh-live-inventory')) return;

  const groups = new Map();
  const numberFormat = new Intl.NumberFormat(document.documentElement.lang || 'en');
  let interval = null;

  const normalizedVariants = (data) => Array.isArray(data?.variants) ? data.variants.filter((variant) => Number.isFinite(Number(variant.id))).map((variant) => ({
    id: String(variant.id),
    title: String(variant.title || ''),
    quantity: Math.max(0, Math.floor(Number(variant.quantity) || 0)),
    available: variant.available === true,
    tracked: variant.tracked === true,
  })) : [];

  const setText = (element, value) => {
    if (element && element.textContent !== value) element.textContent = value;
  };

  const templateText = (template, variant) => String(template || '').replaceAll('__count__', numberFormat.format(variant.quantity)).replaceAll('__color__', variant.title);

  const refreshGroup = async (group, force = false) => {
    if (document.hidden || !group.elements.size || group.pending) return;
    if (!force && Date.now() - group.requestedAt < 15000) return;
    group.requestedAt = Date.now();
    group.pending = true;
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 12000);
    try {
      const url = new URL(group.url, window.location.origin);
      url.searchParams.set('section_id', 'bh-stock-data');
      url.searchParams.set('_bh_stock', String(Date.now()));
      const previewTheme = new URLSearchParams(window.location.search).get('preview_theme_id');
      if (previewTheme) url.searchParams.set('preview_theme_id', previewTheme);
      const response = await fetch(url.toString(), {
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { Accept: 'text/html' },
        signal: controller.signal,
      });
      if (!response.ok) return;
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const dataNode = html.querySelector('[data-bh-stock-data]');
      if (!dataNode) return;
      const payload = JSON.parse(dataNode.textContent);
      if (String(payload.product_id) !== group.id) return;
      const variants = normalizedVariants(payload);
      if (!variants.length) return;
      group.variants = variants;
      group.elements.forEach((element) => {
        element.dataset.stockRefreshedAt = String(Date.now());
        element.renderStock(variants);
      });
    } catch {
    } finally {
      clearTimeout(timeout);
      group.pending = false;
    }
  };

  const refreshAll = () => groups.forEach((group) => refreshGroup(group));

  const ensureInterval = () => {
    if (!interval) interval = window.setInterval(refreshAll, 60000);
  };

  class BumpHeavenLiveInventory extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.selectedId = String(this.dataset.selectedId || '');
      this.threshold = Math.max(1, Number(this.dataset.lowThreshold) || 30);
      this.isCart = this.dataset.inCart === 'true';
      let seed;
      try {
        seed = JSON.parse(this.querySelector('[data-stock-seed]')?.textContent || '{}');
      } catch {
        return;
      }
      this.labels = seed.labels || {};
      const id = String(seed.product_id || this.dataset.productId || '');
      const url = this.dataset.productUrl;
      if (!id || !url) return;
      const localeRoot = window.Shopify?.routes?.root || '/';
      const productPath = url.startsWith(localeRoot) ? url : `${localeRoot.replace(/\/$/, '')}/${url.replace(/^\//, '')}`;
      let group = groups.get(id);
      if (!group) {
        group = { id, url: productPath, elements: new Set(), variants: [], requestedAt: 0, pending: false };
        groups.set(id, group);
      }
      group.url = productPath;
      group.elements.add(this);
      const seedVariants = normalizedVariants(seed);
      if (seedVariants.length) group.variants = seedVariants;
      this.group = group;
      this.renderStock(group.variants);
      ensureInterval();
      refreshGroup(group);
    }

    disconnectedCallback() {
      this.group?.elements.delete(this);
      this.initialized = false;
      if (Array.from(groups.values()).every((group) => !group.elements.size)) {
        clearInterval(interval);
        interval = null;
      }
    }

    renderStock(variants) {
      if (!variants?.length) return;
      this.variants = variants;
      const selected = variants.find((variant) => variant.id === this.selectedId) || variants[0];
      const state = !selected.available ? 'soldout' : selected.tracked && selected.quantity > 0 && selected.quantity <= this.threshold ? 'low' : 'available';
      const message = state === 'soldout' ? this.labels.color_sold_out : !selected.tracked ? this.labels.color_available : state === 'low' ? this.labels.only_left : this.labels.in_stock;
      setText(this.querySelector('[data-stock-selected]'), templateText(message, selected));
      ['available', 'low', 'soldout'].forEach((value) => this.classList.toggle(`bh-stock-panel--${value}`, value === state));
      this.querySelectorAll('[data-stock-variant]').forEach((row) => {
        const variant = variants.find((entry) => entry.id === row.dataset.stockVariant);
        if (!variant) return;
        row.classList.toggle('bh-stock-color--soldout', !variant.available);
        row.classList.toggle('bh-stock-color--selected', variant.id === selected.id);
        const count = !variant.available ? this.labels.sold_out : variant.tracked ? templateText(this.labels.count_left, variant) : this.labels.available;
        setText(row.querySelector('[data-stock-count]'), count);
      });
      const black = variants.find((variant) => variant.title.toLowerCase() === 'black');
      let blackMessage = this.querySelector('[data-stock-black]');
      if (black && !black.available) {
        if (!blackMessage) {
          blackMessage = document.createElement('p');
          blackMessage.className = 'bh-stock-panel__soldout';
          blackMessage.dataset.stockBlack = '';
          this.querySelector('.bh-stock-panel__updated')?.before(blackMessage);
        }
        blackMessage.hidden = false;
        setText(blackMessage, this.labels.black_sold_out || '');
      } else if (blackMessage) {
        blackMessage.hidden = true;
      }
    }
  }

  customElements.define('bh-live-inventory', BumpHeavenLiveInventory);

  document.addEventListener('change', (event) => {
    const picker = event.target.closest?.('variant-radios, variant-selects');
    if (!picker) return;
    let variants;
    try {
      variants = JSON.parse(picker.querySelector('script[type="application/json"]')?.textContent || '[]');
    } catch {
      return;
    }
    const fields = picker.querySelectorAll('fieldset');
    const options = fields.length ? Array.from(fields, (field) => field.querySelector('input:checked')?.value) : Array.from(picker.querySelectorAll('select'), (field) => field.value);
    const selected = variants.find((variant) => variant.options?.every((option, index) => option === options[index]));
    if (!selected) return;
    groups.forEach((group) => {
      if (!group.variants.some((variant) => variant.id === String(selected.id))) return;
      group.elements.forEach((element) => {
        if (element.isCart) return;
        element.selectedId = String(selected.id);
        element.dataset.selectedId = String(selected.id);
        element.renderStock(group.variants);
      });
    });
  });

  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) refreshAll();
  });
  window.addEventListener('focus', refreshAll);
  window.addEventListener('pageshow', refreshAll);
})();
